import type { OrderPricingStrategy } from '@hero/schema';

/**
 * UI-facing categorisation of where a leg's price came from.
 *
 * Persisted code uses {@link OrderPricingStrategy} which is intentionally
 * fine-grained (it has to discriminate `route_seller_own_pricing` from
 * `route_buyer_own_pricing` for audit/analytics). Frontend consumers (badges,
 * banners, locking heuristics in the reorder modal) almost never care about
 * that level of detail — they care which of these four buckets the price
 * sits in:
 *
 *   - `'agreement'`   The buyer and seller have a directed Route between
 *                     them; the price was looked up from that lane.
 *                     Reorder logic: do **not** lock the price, let the
 *                     resolver re-pick from the agreement on the new order
 *                     (the agreement may have been updated since).
 *
 *   - `'everyone'`    A "public" Route — either the seller's published rate
 *                     or the buyer's default budget. No bilateral
 *                     agreement was needed.
 *                     Reorder logic: same as `agreement` — let the resolver
 *                     re-pick the current public rate.
 *
 *   - `'one_time'`    The caller passed a one-shot price (reorder modal,
 *                     edit-price drawer, or super-admin override). No Route
 *                     match was used; the number was taken at face value.
 *                     Reorder logic: forward the same price as `route.price`
 *                     to lock it (the user is replicating an off-Route deal).
 *
 *   - `'unresolved'`  No Route matched and no one-time price was provided;
 *                     the leg was persisted with subtotal `0`, awaiting
 *                     manual entry via the edit-price drawer.
 *                     Reorder logic: if the realised price is still 0,
 *                     don't forward (let the resolver try again on the new
 *                     pair). If a price was filled in later, forward it as a
 *                     one-time lock.
 *
 * Use this selector everywhere the UI needs to branch on "what kind of
 * price is this?" — never re-implement the strategy → category mapping
 * inline. New `OrderPricingStrategy` values must extend the switch so the
 * compiler catches missed cases.
 *
 * @example
 * const category = getPriceCategory(order.contracts[0]);
 * if (category === 'one_time' || (category === 'unresolved' && leg.amount.realizedSubtotal > 0)) {
 *   // forward this leg's price to the new order
 * }
 */
export type PriceCategory = 'agreement' | 'everyone' | 'one_time' | 'unresolved';

/**
 * Anything carrying a `pricingStrategy` field — typically an
 * `Order.contracts[i]` leg or an `Offer.targets[i].pricing` entry. The
 * function only reads the strategy, so a bare `{ pricingStrategy }` works
 * too in tests.
 */
export type PriceCategoryInput =
  | { pricingStrategy?: OrderPricingStrategy | null }
  | OrderPricingStrategy
  | null
  | undefined;

function readStrategy(input: PriceCategoryInput): OrderPricingStrategy | undefined {
  if (input == null) return undefined;
  if (typeof input === 'string') return input;
  return input.pricingStrategy ?? undefined;
}

/**
 * Map an `OrderPricingStrategy` (or any object carrying one) to the four
 * UI-facing categories. Returns `'unresolved'` for missing / unknown values
 * so callers always get a defined branch to render against.
 */
export function getPriceCategory(input: PriceCategoryInput): PriceCategory {
  const strategy = readStrategy(input);
  switch (strategy) {
    // legacy / deprecated values that should round-trip to "agreement":
    // platform_route_pricing was the broker-era directed price, and
    // override_route_directed_pricing was a directed Route with a manual
    // tweak — both are best surfaced as "agreement" until the migration
    // backfills them.
    case 'route_directed_pricing':
    case 'platform_route_pricing':
    case 'override_route_directed_pricing':
      return 'agreement';

    case 'route_seller_own_pricing':
    case 'route_buyer_own_pricing':
      return 'everyone';

    case 'one_time_pricing':
    case 'admin_custom_pricing':
      return 'one_time';

    // legacy alias — distance_based_pricing was always paired with
    // subtotal 0 and is functionally identical to `unresolved_pricing`.
    case 'unresolved_pricing':
    case 'distance_based_pricing':
    case undefined:
      return 'unresolved';
  }
}
